import { isSendShortcut } from '../lib/composer_enter.js'
/**
 * MentionAutocomplete hook for @ mention tab search.
 *
 * Responsibilities:
 * - Detect @ at word boundaries
 * - Calculate cursor position for dropdown
 * - Filter tabs client-side
 * - Handle keyboard navigation
 * - Insert mention markup
 */
export default {
  mounted () {
    // The hook is attached to the parent div, find the contenteditable
    this.editor = this.el.querySelector('[contenteditable="true"]')
    if (!this.editor) {
      console.warn('[MentionAutocomplete] Contenteditable not found')
      return
    }

    // State
    this.tabList = []
    this.mentionStart = null
    this.isAutocompleteVisible = false
    this.selectedIndex = 0
    this.filteredTabs = []
    this.savedRange = null // Store the range when autocomplete is shown

    // Bind event listeners
    this.handleInput = this.handleInput.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)

    this.editor.addEventListener('input', this.handleInput)
    // Use capture phase to intercept keydown before form submission
    this.editor.addEventListener('keydown', this.handleKeydown, true)

    // Listen for tab list from LiveView
    this.handleEvent('tab_list_cached', (data) => {
      this.tabList = data.tabs || []

      // Re-filter with cached tabs
      if (this.isAutocompleteVisible) {
        this.filterAndUpdate()
      }
    })

    // Listen for insert command from LiveView
    this.handleEvent('insert_mention', (data) => {
      this.insertMention(data.tab_id, data.tab_title)
    })
    this.handleEvent('insert_settings_mention', () => {
      this.insertMention(null, 'settings')
    })

    // Brainbox scopes (`data-scopes`): the server searches tabs, apps, groups
    // and Spaces and renders them; this only needs how many there are.
    this.scopeHeadItems = []
    this.handleEvent('scope_mention_results', ({ count }) => {
      this.filteredTabs = [...this.scopeHeadItems, ...Array.from({ length: count || 0 }, () => ({ kind: 'scope' }))]
      this.selectedIndex = Math.min(this.selectedIndex, Math.max(this.filteredTabs.length - 1, 0))
    })
  },

  /**
   * Whether the composer is in Brainbox scopes mode, where `@` picks tabs,
   * apps, groups and Spaces from the server instead of the tab list.
   * @returns {boolean}
   */
  scopesMode () {
    return this.el.dataset?.scopes === 'true'
  },

  /**
   * Handles input changes in contenteditable.
   * Detects @ mentions at word boundaries.
   */
  handleInput () {
    // Off on Chat with Settings (see `input_area.ex`). Checked per keystroke
    // because switching chats flips the attribute without remounting.
    if (this.el.hasAttribute('data-mentions-off')) {
      if (this.isAutocompleteVisible) {this.hideAutocomplete()}
      return
    }

    // Get text content before cursor (excluding mention spans)
    const beforeCursor = this.getTextBeforeCursor()

    if (beforeCursor === null) {
      return
    }

    // Once the routing prefix is complete, the rest is the question, not a
    // tab search. In particular Enter must go back to the normal send path.
    if (/^\s*@settings\s/i.test(beforeCursor)) {
      this.hideAutocomplete()
      return
    }

    // Match @ at word boundary followed by any alphanumeric/space/dash chars
    const match = beforeCursor.match(/(?:^|\s|\n)@([\w\s-]*)$/)

    if (match) {
      // Found @ mention
      const query = match[1]

      // Show autocomplete
      if (!this.isAutocompleteVisible) {
        this.isAutocompleteVisible = true

        // Save the current range/selection so we can restore it later
        this.savedRange = this.captureValidRange()

        // Calculate dropdown position
        const position = this.calculateCursorPosition()

        // Push event to LiveView to show autocomplete
        this.pushEvent('show_autocomplete', {
          query: query,
          position: position
        })
        this.filterAndUpdate(query)
      } else {
        // Already visible, just update filter and update saved range
        this.savedRange = this.captureValidRange()
        this.filterAndUpdate(query)
      }
    } else {
      // No @ mention detected
      if (this.isAutocompleteVisible) {
        this.hideAutocomplete()
      }
    }
  },

  /**
   * Captures a valid range that's inside the contenteditable.
   * Returns null if no valid range can be captured.
   */
  captureValidRange () {
    const selection = window.getSelection()
    if (!selection.rangeCount) {
      return null
    }

    const range = selection.getRangeAt(0)

    // Check if the range is inside the contenteditable
    let container = range.startContainer
    let isInsideEditor = false

    // Walk up the DOM tree to see if we're inside the editor
    while (container) {
      if (container === this.editor) {
        isInsideEditor = true
        break
      }
      container = container.parentNode
    }

    if (!isInsideEditor) {
      // Range is outside the editor, create a new one at the end of editor content
      const newRange = document.createRange()

      // Find the last text node or position in editor
      if (this.editor.lastChild) {
        if (this.editor.lastChild.nodeType === Node.TEXT_NODE) {
          newRange.setStart(this.editor.lastChild, this.editor.lastChild.length)
          newRange.setEnd(this.editor.lastChild, this.editor.lastChild.length)
        } else {
          newRange.setStart(this.editor, this.editor.childNodes.length)
          newRange.setEnd(this.editor, this.editor.childNodes.length)
        }
      } else {
        newRange.setStart(this.editor, 0)
        newRange.setEnd(this.editor, 0)
      }

      return newRange
    }

    return range.cloneRange()
  },

  /**
   * Gets text content before the cursor position in contenteditable.
   * Replaces mention spans with empty string to avoid false @ detection.
   */
  getTextBeforeCursor () {
    const selection = window.getSelection()
    if (!selection.rangeCount) {return null}

    const range = selection.getRangeAt(0)
    const preCaretRange = range.cloneRange()
    preCaretRange.selectNodeContents(this.editor)
    preCaretRange.setEnd(range.endContainer, range.endOffset)

    // Extract text but skip mention spans
    const fragment = preCaretRange.cloneContents()
    let text = ''

    const extractText = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip mention spans
        if (node.classList.contains('mention')) {
          return
        }
        // Process children
        for (const child of node.childNodes) {
          extractText(child)
        }
      }
    }

    for (const node of fragment.childNodes) {
      extractText(node)
    }

    return text
  },

  /**
   * Handles keyboard events for navigation.
   */
  handleKeydown (e) {
    // Only intercept if autocomplete is visible
    if (!this.isAutocompleteVisible) {return}

    // Skip while an IME (e.g. Japanese/Chinese/Korean) is composing.
    if (e.isComposing || e.keyCode === 229) {return}

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        this.pushEvent('autocomplete_navigate', { direction: 'down' })
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredTabs.length - 1)
        this.scrollToSelected()
        break

      case 'ArrowUp':
        e.preventDefault()
        this.pushEvent('autocomplete_navigate', { direction: 'up' })
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.scrollToSelected()
        break

      case 'Enter':
        // Only if autocomplete is visible and not shift+enter (newline). Ctrl/Cmd+Enter
        // is the always-send shortcut, so it passes through to the composer.
        if (!e.shiftKey && !isSendShortcut(e)) {
          e.preventDefault()
          e.stopPropagation()
          this.pushEvent('autocomplete_select', { index: this.selectedIndex })
        }
        break

      case 'Escape':
        e.preventDefault()
        this.hideAutocomplete()
        break

      case 'Tab':
        // Allow tab to select as well
        e.preventDefault()
        e.stopPropagation()
        this.pushEvent('autocomplete_select', { index: this.selectedIndex })
        break
    }
  },

  /**
   * Filters tabs based on query and pushes to LiveView.
   */
  filterAndUpdate (query) {
    if (query === undefined) {
      // Extract query from current contenteditable
      const beforeCursor = this.getTextBeforeCursor()
      const match = beforeCursor ? beforeCursor.match(/(?:^|\s|\n)@([\w\s-]*)$/) : null
      query = match ? match[1] : ''
    }

    // Determine mode based on query length
    const isSearchMode = query.length >= 1
    let results

    if (this.scopesMode()) {
      this.filterScopes(query, isSearchMode)
      return
    }

    if (isSearchMode) {
      // Search mode: filter all tabs
      results = this.filterTabs(this.tabList, query)
    } else {
      // Recent mode: show 5 most recent tabs
      results = this.tabList
        .sort((a, b) => (b.lastAccessed || 0) - (a.lastAccessed || 0))
        .slice(0, 5)
    }

    // Settings is a routing prefix, so only suggest it at the start of a
    // message. It needs no tab-list response and never becomes an attachment.
    const beforeCursor = this.getTextBeforeCursor() || ''
    const settingsQuery = query.trim().toLowerCase()
    if (/^\s*@[^@]*$/.test(beforeCursor) &&
        (!settingsQuery || 'settings'.startsWith(settingsQuery) ||
         'chat with settings'.startsWith(settingsQuery) || settingsQuery === 'cws')) {
      results = [{ kind: 'settings', title: 'Chat with Settings' }, ...results]
    }

    this.filteredTabs = results
    this.selectedIndex = 0

    // Push to LiveView
    this.pushEvent('update_filtered_tabs', {
      filtered_tabs: results,
      query: query,
      is_search_mode: isSearchMode
    })
  },

  /**
   * Scopes mode: the routing prefix stays local, everything else is searched
   * on the server (debounced), which answers with `scope_mention_results`.
   * @param {string} query - the text after `@`
   * @param {boolean} isSearchMode - whether anything was typed
   */
  filterScopes (query, isSearchMode) {
    this.scopeHeadItems = this.settingsItem(query)
    this.filteredTabs = [...this.scopeHeadItems]
    this.selectedIndex = 0

    this.pushEvent('update_filtered_tabs', {
      filtered_tabs: this.scopeHeadItems,
      query,
      is_search_mode: isSearchMode
    })

    clearTimeout(this.scopeQueryTimer)
    this.scopeQueryTimer = setTimeout(() => {
      this.pushEvent('scope_mention_query', { query })
    }, 150)
  },

  /**
   * The `@settings` routing prefix, offered only at the start of a message.
   * @param {string} query - the text after `@`
   * @returns {Array<Object>} the item, or nothing
   */
  settingsItem (query) {
    const beforeCursor = this.getTextBeforeCursor() || ''
    const settingsQuery = query.trim().toLowerCase()
    if (/^\s*@[^@]*$/.test(beforeCursor) &&
        (!settingsQuery || 'settings'.startsWith(settingsQuery) ||
         'chat with settings'.startsWith(settingsQuery) || settingsQuery === 'cws')) {
      return [{ kind: 'settings', title: 'Chat with Settings' }]
    }
    return []
  },

  /**
   * Filters tabs by query using token-based matching.
   * Supports typo tolerance via edit distance.
   * Called when query.length >= 1 (search mode).
   */
  filterTabs (tabs, query) {
    if (!query || query.trim().length < 1) {
      // Should not reach here, but fallback to recent tabs
      return tabs.slice(0, 5)
    }

    const queryTokens = query.toLowerCase().trim().split(/\s+/)

    const scored = tabs.map((tab) => {
      const title = (tab.title || tab.url || '').toLowerCase()
      const url = (tab.url || '').toLowerCase()
      const combined = title + ' ' + url

      // Token-based matching: all query tokens must match
      let score = 0
      let allTokensMatch = true

      for (const queryToken of queryTokens) {
        let tokenMatched = false

        // Check exact substring match first
        if (combined.includes(queryToken)) {
          score += 10
          tokenMatched = true
        } else {
          // Check fuzzy match (simple edit distance for typo tolerance)
          const fuzzyMatch = this.findFuzzyMatch(combined, queryToken)
          if (fuzzyMatch) {
            score += 5
            tokenMatched = true
          }
        }

        if (!tokenMatched) {
          allTokensMatch = false
          break
        }
      }

      // Boost score if title starts with query
      if (title.startsWith(query.toLowerCase())) {
        score += 20
      }

      return {
        tab: tab,
        score: allTokensMatch ? score : -1
      }
    })

    // Filter out non-matches and sort by score
    const filtered = scored
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.tab)
      .slice(0, 100) // Max 100 results

    return filtered
  },

  /**
   * Simple fuzzy matching for typo tolerance.
   * Returns true if queryToken closely matches any word in text.
   */
  findFuzzyMatch (text, queryToken) {
    const words = text.split(/\s+/)

    for (const word of words) {
      if (this.editDistance(word, queryToken) <= 1 && queryToken.length >= 4) {
        return true
      }
    }

    return false
  },

  /**
   * Calculate Levenshtein edit distance between two strings.
   * Used for typo tolerance (e.g., "goggle" matches "google").
   */
  editDistance (a, b) {
    if (a.length === 0) {return b.length}
    if (b.length === 0) {return a.length}

    const matrix = []

    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i]
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          )
        }
      }
    }

    return matrix[b.length][a.length]
  },

  /**
   * Calculate position to align popup bottom with editor top.
   */
  calculateCursorPosition () {
    const editor = this.editor
    const editorRect = editor.getBoundingClientRect()

    const dropdownMaxWidth = 480
    const padding = 20

    // Calculate x position - center horizontally on editor, but stay in viewport
    let x = editorRect.left
    if (x + dropdownMaxWidth > window.innerWidth - padding) {
      x = Math.max(padding, window.innerWidth - dropdownMaxWidth - padding)
    }

    // Position at the top of the editor
    // CSS will use transform: translateY(-100%) to position bottom at this point
    const y = editorRect.top

    return { x, y }
  },

  /**
   * Scrolls selected item into view.
   */
  scrollToSelected () {
    const selectedEl = document.getElementById(`tab-autocomplete-item-${this.selectedIndex}`)
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  },

  /**
   * Gets plain text content of the editor, skipping mention spans.
   * Ensures offset consistency with walkTextNodes in insertMention.
   */
  getPlainTextContent () {
    let text = ''

    const extractText = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList && node.classList.contains('mention')) {
          return
        }
        for (const child of node.childNodes) {
          extractText(child)
        }
      }
    }

    for (const node of this.editor.childNodes) {
      extractText(node)
    }

    return text
  },

  /**
   * Inserts mention as a styled span element.
   * Called from LiveView after tab selection.
   */
  insertMention (tabId, tabTitle) {
    const isSettings = tabId === null && tabTitle === 'settings'
    // Get text content from editor, excluding mention spans
    // This must be consistent with walkTextNodes below which also skips mentions
    const currentText = this.getPlainTextContent()

    // Find the @ mention in the current text
    const match = currentText.match(/(?:^|\s|\n)@([\w\s-]*)$/)

    if (!match) {
      this.hideAutocomplete()
      return
    }

    // Find the position of the @ in the editor
    const atIndex = currentText.lastIndexOf(match[0])
    const actualAtIndex = atIndex + (match[0].startsWith(' ') || match[0].startsWith('\n') ? 1 : 0)

    // Create a range to select and delete the @ and query text
    const selection = window.getSelection()
    const range = document.createRange()

    // Find the text node containing the @
    let textNode = null
    let nodeOffset = 0
    let currentOffset = 0

    // Walk through text nodes to find the right position
    const walkTextNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const nodeLength = node.textContent.length
        if (currentOffset + nodeLength > actualAtIndex && !textNode) {
          textNode = node
          nodeOffset = actualAtIndex - currentOffset
        }
        currentOffset += nodeLength
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip mention spans
        if (node.classList && node.classList.contains('mention')) {
          return
        }
        for (const child of node.childNodes) {
          walkTextNodes(child)
        }
      }
    }

    walkTextNodes(this.editor)

    if (!textNode) {
      console.error('[MentionAutocomplete] Could not find text node with @')
      this.hideAutocomplete()
      return
    }

    // Delete the @ and query text
    try {
      range.setStart(textNode, nodeOffset)
      if (isSettings && this.savedRange) {
        range.setEnd(this.savedRange.endContainer, this.savedRange.endOffset)
      } else {
        range.setEnd(textNode, textNode.textContent.length)
      }
      range.deleteContents()
    } catch (err) {
      console.error('[MentionAutocomplete] Error deleting range:', err)
      this.hideAutocomplete()
      return
    }

    // Create mention span element
    const mention = isSettings
      ? document.createTextNode('@settings')
      : document.createElement('span')
    if (!isSettings) {
      mention.className = 'mention'
      mention.contentEditable = 'false'
      mention.dataset.tabId = tabId
      mention.dataset.title = tabTitle
      mention.textContent = '@' + tabTitle
    }

    // Insert mention at the current range position
    range.insertNode(mention)

    // A plain trailing space can collapse in contenteditable, placing the
    // next typed character straight after @settings. Keep it visible to the
    // caret; ContentEditableInput normalizes it back to a regular space.
    const space = document.createTextNode(isSettings ? '\u00a0' : ' ')
    mention.parentNode.insertBefore(space, mention.nextSibling)

    // Move cursor after space
    range.setStart(space, space.length)
    range.setEnd(space, space.length)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)

    // Trigger input event to sync with hidden input
    this.editor.dispatchEvent(new Event('input', { bubbles: true }))

    // Refocus the editor so user can continue typing
    this.editor.focus()

    // Hide autocomplete
    this.hideAutocomplete()
  },

  /**
   * Hides the autocomplete dropdown.
   */
  hideAutocomplete () {
    if (this.isAutocompleteVisible) {
      this.isAutocompleteVisible = false
      this.mentionStart = null
      this.selectedIndex = 0
      this.filteredTabs = []
      this.savedRange = null // Clear saved range
      this.pushEvent('hide_autocomplete', {})
    }
  },

  destroyed () {
    clearTimeout(this.scopeQueryTimer)
    if (this.editor) {
      this.editor.removeEventListener('input', this.handleInput)
      this.editor.removeEventListener('keydown', this.handleKeydown, true)
    }
  }
}
